/**
 * Analytics Hooks
 *
 * TanStack Query hooks for analytics data including:
 * - Trend data for time-series charts
 * - Comparison data for tables (departments, operators, SOPs)
 * - Individual operator analytics
 * - CSV/PDF export
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// Query keys
export const analyticsKeys = {
  all: ['analytics'] as const,
  trends: (params: TrendParams) => [...analyticsKeys.all, 'trends', params] as const,
  comparison: (params: ComparisonParams) => [...analyticsKeys.all, 'comparison', params] as const,
  operator: (id: string, period: number) => [...analyticsKeys.all, 'operator', id, period] as const,
};

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface TrendParams {
  period: number; // days: 7, 30, 90, 365
  groupBy: 'day' | 'week' | 'month';
  departmentId?: string;
}

export interface ComparisonParams {
  compareBy: 'department' | 'operator' | 'sop';
  period: number;
  departmentId?: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TrendDataPoint {
  date: string;
  overall: number | null;
  compliance: number | null;
  efficiency: number | null;
  safety: number | null;
  assessmentCount: number;
}

export interface TrendResponse {
  data: TrendDataPoint[];
  summary: {
    averageOverall: number | null;
    averageCompliance: number | null;
    averageEfficiency: number | null;
    averageSafety: number | null;
    totalAssessments: number;
    periodStart: string;
    periodEnd: string;
  };
}

export interface ComparisonItem {
  id: string;
  name: string;
  rank: number;
  overall: number | null;
  compliance: number | null;
  efficiency: number | null;
  safety: number | null;
  assessmentCount: number;
  trend: 'improving' | 'declining' | 'stable' | 'insufficient_data';
  change: number | null; // percentage change from previous period
}

export interface ComparisonResponse {
  data: ComparisonItem[];
  compareBy: 'department' | 'operator' | 'sop';
  period: number;
  totalItems: number;
}

export interface OperatorAnalyticsData {
  operator: {
    id: string;
    firstName: string;
    lastName: string;
    employeeId: string;
    departmentName: string | null;
  };
  summary: {
    overallScore: number | null;
    complianceScore: number | null;
    efficiencyScore: number | null;
    safetyScore: number | null;
    totalAssessments: number;
    trend: 'improving' | 'declining' | 'stable' | 'insufficient_data';
    percentileRank: number | null; // e.g., 85 means better than 85% of operators
  };
  progressionData: {
    date: string;
    overall: number | null;
    compliance: number | null;
    efficiency: number | null;
    safety: number | null;
  }[];
  sopBreakdown: {
    sopId: string;
    sopTitle: string;
    assessmentCount: number;
    averageScore: number | null;
    lastAssessedAt: string | null;
    trend: 'improving' | 'declining' | 'stable' | 'insufficient_data';
  }[];
  recentAssessments: {
    id: string;
    sopTitle: string;
    overallScore: number;
    assessedAt: string;
    assessmentType: 'auto' | 'manual';
  }[];
}

export type ExportFormat = 'csv' | 'pdf';
export type ExportType = 'trends' | 'comparison' | 'operator';

// ============================================
// TREND QUERIES
// ============================================

/**
 * Fetch trend data for time-series charts
 */
export function useAnalyticsTrends(params: TrendParams) {
  const api = useApi();

  return useQuery({
    queryKey: analyticsKeys.trends(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('period', String(params.period));
      searchParams.set('groupBy', params.groupBy);
      if (params.departmentId) {
        searchParams.set('departmentId', params.departmentId);
      }

      return api.get<TrendResponse>(`/api/analytics/trends?${searchParams.toString()}`);
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// ============================================
// COMPARISON QUERIES
// ============================================

/**
 * Fetch comparison data for tables
 */
export function useAnalyticsComparison(params: ComparisonParams) {
  const api = useApi();

  return useQuery({
    queryKey: analyticsKeys.comparison(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('compareBy', params.compareBy);
      searchParams.set('period', String(params.period));
      if (params.departmentId) {
        searchParams.set('departmentId', params.departmentId);
      }
      if (params.limit) {
        searchParams.set('limit', String(params.limit));
      }
      if (params.sortBy) {
        searchParams.set('sortBy', params.sortBy);
      }
      if (params.sortOrder) {
        searchParams.set('sortOrder', params.sortOrder);
      }

      return api.get<ComparisonResponse>(`/api/analytics/comparison?${searchParams.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================
// OPERATOR ANALYTICS
// ============================================

/**
 * Fetch individual operator analytics
 */
export function useOperatorAnalytics(operatorId: string | null, period: number = 365) {
  const api = useApi();

  return useQuery({
    queryKey: analyticsKeys.operator(operatorId ?? '', period),
    queryFn: () =>
      api.get<OperatorAnalyticsData>(`/api/analytics/operator/${operatorId}?period=${period}`),
    enabled: !!operatorId,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================
// EXPORT
// ============================================

interface ExportParams {
  format: ExportFormat;
  type: ExportType;
  // Additional params based on type
  period?: number;
  groupBy?: string;
  compareBy?: string;
  departmentId?: string;
  operatorId?: string;
}

/**
 * Export analytics data as CSV or PDF
 */
export function useExportAnalytics() {
  const api = useApi();

  return useMutation({
    mutationFn: async (params: ExportParams) => {
      const searchParams = new URLSearchParams();
      searchParams.set('format', params.format);
      searchParams.set('type', params.type);
      if (params.period) searchParams.set('period', String(params.period));
      if (params.groupBy) searchParams.set('groupBy', params.groupBy);
      if (params.compareBy) searchParams.set('compareBy', params.compareBy);
      if (params.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params.operatorId) searchParams.set('operatorId', params.operatorId);

      const blob = await api.getBlob(`/api/analytics/export?${searchParams.toString()}`);
      return { blob, params };
    },
    onSuccess: ({ blob, params }) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const extension = params.format === 'pdf' ? 'pdf' : 'csv';
      const timestamp = new Date().toISOString().split('T')[0];
      a.download = `analytics-${params.type}-${timestamp}.${extension}`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });
}
